import React from "react";
import { Box, TableCell, Stack, Checkbox } from "@mui/material";
import CommonTable, { ReadOnlyCell } from "../../utils/CommonTable";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import GroupedSelect from "../../utils/GroupedSelect";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import {
  incomeTypes,
  DEFAULT_COUNTRY,
} from "../ForeignIncomeTracker/calculations";
import CountryField from "../ForeignIncomeTracker/CountryField";
import TaxField from "../../utils/TextField/TaxField";
import { mappedCurrencyList } from "../../../utils/static/currency";
import { JustInfo } from "../../utils/Icon";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { getForeignIncomeCurrencyDetails } from "./calculation";

const ForeignIncomeTable = ({
  rows,
  onChange,
  onRemove,
  functionalCurrency = "SGD",
  functionalCurrencyRate = 1,
  totals = {},
  onLockRow,
  onUnlockRow,
  canEdit = true,
}) => {
  const getCurrencyDetails = (row) =>
    getForeignIncomeCurrencyDetails(
      row,
      functionalCurrency,
      functionalCurrencyRate
    );

  const getTableColumns = () => [
    {
      key: "scheduleName",
      label: "Description",
      headerAlign: "left",
      colAlign: "left",
      render: (row) => (
        <Box sx={{ minWidth: 150 }}>
          {!row.isPopulated ? (
            <TextFieldForName
              value={row.scheduleName || ""}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "scheduleName",
              }}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          ) : (
            <Box sx={{ fontWeight: 500, color: "text.primary" }}>
              {row.source?.sch_name || ""}
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: "incomeType",
      label: "Income Type",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <GroupedSelect
          value={row.incomeType || ""}
          options={incomeTypes}
          onChange={(e) =>
            onChange(e.target.value, {
              rowId: row.rowId,
              field: "incomeType",
            })
          }
          enableSearch
          disabled={row.isLocked}
          canEdit={canEdit}
        />
      ),
    },
    {
      key: "country",
      label: "Country",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <CountryField
          key={row.rowId}
          country={row.country}
          onChange={(value) =>
            onChange(value, { rowId: row.rowId, field: "country" })
          }
          unspecifiedValue={DEFAULT_COUNTRY}
          disabled={row.isLocked}
          canEdit={canEdit}
        />
      ),
    },
    {
      key: "grossIncome",
      label: "Gross Income",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <Box sx={{ minWidth: 80 }}>
          {!row.isPopulated ? (
            <TaxField
              value={row.grossIncome}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "grossIncome",
                fieldType: "numeric",
              }}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          ) : (
            <ReadOnlyCell
              value={formatAmount(row.grossIncome)}
              width='100%'
              textAlign='right'
            />
          )}
        </Box>
      ),
    },
    {
      key: "allowableExpenses",
      label: "Allowable Expenses",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <Box sx={{ minWidth: 120 }}>
          {row.isPopulated ? (
            <ReadOnlyCell
              value={formatAmount(row.allowableExpenses)}
              width='100%'
              textAlign='right'
            />
          ) : (
            <TaxField
              value={row.allowableExpenses}
              onChange={onChange}
              maxValue={row.grossIncome}
              maxValueMessage='Allowable expenses cannot exceed gross income'
              reference={{
                rowId: row.rowId,
                field: "allowableExpenses",
                fieldType: "numeric",
              }}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          )}
        </Box>
      ),
    },
    {
      key: "netIncome",
      label: "Net Income",
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <Box
          sx={{ fontWeight: 500, color: "text.primary", textAlign: "right" }}
        >
          {formatAmount(row.netIncome)}
        </Box>
      ),
    },
    ...(functionalCurrency !== "SGD"
      ? [
          {
            key: "functionalCurrency",
            label: "Currency",
            headerAlign: "center",
            colAlign: "center",
            render: (row) => (
              <Box sx={{ minWidth: 150 }}>
                {row.isPopulated ? (
                  <ReadOnlyCell
                    value={getCurrencyDetails(row).currency}
                    width='100%'
                  />
                ) : (
                  <GroupedSelect
                    value={getCurrencyDetails(row).currency}
                    onChange={(e) =>
                      onChange(e.target.value, {
                        rowId: row.rowId,
                        field: "functionalCurrency",
                      })
                    }
                    options={mappedCurrencyList}
                    enableSearch
                    disabled={row.isLocked}
                    canEdit={canEdit}
                  />
                )}
              </Box>
            ),
          },
          {
            key: "exchangeRate",
            label: "Exchange Rate",
            headerAlign: "center",
            colAlign: "center",
            render: (row) => {
              const { currency, exchangeRate } = getCurrencyDetails(row);
              return (
                <Box
                  sx={{ minWidth: 160, display: "flex", alignItems: "center" }}
                >
                  {row.isPopulated ? (
                    <ReadOnlyCell
                      value={exchangeRate}
                      width='100%'
                    />
                  ) : (
                    <TaxField
                      value={exchangeRate}
                      onChange={onChange}
                      reference={{
                        rowId: row.rowId,
                        field: "exchangeRate",
                        fieldType: "numeric",
                      }}
                      decimalScale={2}
                      disabled={row.isLocked}
                      canEdit={canEdit}
                    />
                  )}

                  {Boolean(currency && exchangeRate && currency !== "SGD") && (
                    <JustInfo
                      title={`1 ${currency} = ${exchangeRate} SGD`}
                      ml={1}
                    />
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
    {
      key: "foreignTaxSuffered",
      label: `Foreign Tax Suffered${
        functionalCurrency !== "SGD" ? ` (${functionalCurrency})` : ""
      }`,
      headerAlign: "right",
      colAlign: "right",
      render: (row) => (
        <Box sx={{ minWidth: 120 }}>
          {row.isPopulated ? (
            <ReadOnlyCell
              value={formatAmount(row.foreignTaxSuffered, false)}
              width='100%'
              textAlign='right'
            />
          ) : (
            <TaxField
              value={row.foreignTaxSuffered}
              onChange={onChange}
              reference={{
                rowId: row.rowId,
                field: "foreignTaxSuffered",
                fieldType: "numeric",
              }}
              decimalScale={2}
              disabled={row.isLocked}
              canEdit={canEdit}
            />
          )}
        </Box>
      ),
    },
    ...(functionalCurrency !== "SGD"
      ? [
          {
            key: "foreignTaxSufferedSGD",
            label: "Foreign Tax (SGD)",
            headerAlign: "right",
            colAlign: "right",
            render: (row) => (
              <ReadOnlyCell
                value={formatAmount(row.foreignTaxSufferedSGD, false)}
                width='100%'
                textAlign='right'
              />
            ),
          },
        ]
      : []),
    {
      key: "ftcPooling",
      label: "FTC Pooling",
      headerAlign: "center",
      colAlign: "center",
      render: (row) =>
        canEdit ? (
          <Checkbox
            checked={row.ftcPooling || false}
            onChange={(e) =>
              onChange(e.target.checked, {
                rowId: row.rowId,
                field: "ftcPooling",
              })
            }
          />
        ) : (
          <TextFieldForViewOnly value={row.ftcPooling ? "Yes" : "No"} />
        ),
    },
    {
      key: "reference",
      label: "Reference",
      headerAlign: "center",
      colAlign: "center",
      render: (row) => (
        <TextFieldForName
          value={row.reference || ""}
          onChange={onChange}
          reference={{
            rowId: row.rowId,
            field: "reference",
          }}
          disabled={row.isLocked}
          canEdit={canEdit}
          viewOnlyAlign='center'
        />
      ),
    },
    {
      key: "source",
      label: "Sch",
      headerAlign: "center",
      render: (row) => <ReadOnlyCell value={row.source?.sch_no || ""} />,
    },
    canEdit
      ? {
          key: "actions",
          label: "Actions",
          responsiveWidth: "112px",
          headerAlign: "center",
          colAlign: "center",
          render: (row) => (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='center'
              sx={{
                px: 1,
                py: 0.5,
                "& .MuiIconButton-root": { flexShrink: 0 },
              }}
            >
              {row.isLocked ? (
                <IconButtonWithTooltip
                  icon={LockOpenIcon}
                  tooltipTitle='Unlock the row'
                  size='small'
                  onClick={() => onUnlockRow(row.rowId)}
                  color='success'
                />
              ) : (
                <IconButtonWithTooltip
                  icon={LockIcon}
                  tooltipTitle='Lock the row'
                  onClick={() => onLockRow(row.rowId)}
                  size='small'
                  disabled={!row.incomeType || !row.country}
                  color='warning'
                />
              )}
              <IconButtonWithTooltip
                icon={DeleteOutlineIcon}
                tooltipTitle='Delete Row'
                onClick={() => onRemove(row.rowId)}
                color='error'
                size='small'
                disabled={row.isPopulated || row.isLocked}
              />
            </Stack>
          ),
        }
      : [],
  ];

  return (
    <CommonTable
      rows={rows}
      columns={getTableColumns()}
      noRowsHint='No foreign income data available yet.'
    >
      <TableCell colSpan={2} />
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        Total
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.grossIncome || 0)}
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.allowableExpenses || 0)}
      </TableCell>
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.netIncome || 0)}
      </TableCell>
      {functionalCurrency !== "SGD" && (
        <>
          <TableCell />
          <TableCell />
        </>
      )}
      <TableCell align='right' sx={{ fontWeight: "bold" }}>
        {formatAmount(totals?.foreignTaxSuffered || 0, false)}
      </TableCell>
      {functionalCurrency !== "SGD" && (
        <TableCell align='right' sx={{ fontWeight: "bold" }}>
          {formatAmount(totals?.foreignTaxSufferedSGD || 0, false)}
        </TableCell>
      )}
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
    </CommonTable>
  );
};

export default ForeignIncomeTable;
